import { List, Record, Map } from "immutable";
import { AnyAction } from "redux";

import { defaultValues } from "@config/defaultValues";
import { IS_BROWSER } from "@config/consts";

import { BookingAvailability } from "@typings/booking";
import { PremisesEquipments, B2BPricing } from "@typings/b2b";
import { PROMO_CODE_NOTIFICATION, PAYMENT_METHODS } from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import { getQueryValue } from "@services/QueryParamsParser";
import { extractHourMinute } from "@services/DateFormatter";
import {
  ACTION_FAIL,
  ACTION_NO_DATA,
  ACTION_PENDING,
  ACTION_SUCCESS,
  UnifiedErrors,
} from "@services/AsyncActionCreatorFactory";

import { B2BReducer, RegistrationForm } from "./b2b.typings";
import { B2B_ACTIONS } from "./b2b.actions";
import {
  bookingAvailabilityFactory,
  agentsFactory,
  b2bTasks,
  pricingInitialState,
  premisesEquipmentsInitialState,
  premisesEquipmentsFactory,
  bookingAvailabilityInitialState,
  pricingFactory,
  locationsFactory,
  premisesServicesFactory,
  bookingCustomAvailabilityFactory,
  frequenciesFactory,
  pricePlanFactory,
  pricePlansFactory,
} from "./b2b.factories";

const getServiceLocationIdFromUrl = (url: string) =>
  !isNaN(Number(getQueryValue(url, "serviceLocationId")))
    ? Number(getQueryValue(url, "serviceLocationId"))
    : defaultValues.b2b.serviceLocationId;

export const initialState = Record<B2BReducer>({
  calendar: Record<BookingAvailability>(bookingAvailabilityInitialState)(),
  premisesServices: null,
  premisesServicesFetched: defaultValues.b2b.premisesServicesFetched,
  premises: defaultValues.b2b.premises,
  surfaceArea: defaultValues.b2b.surfaceArea,
  premisesEquipments: Record<PremisesEquipments>(
    premisesEquipmentsInitialState,
  )(),
  recommendedDurationsApplied: defaultValues.b2b.recommendedDurationsApplied,
  recommendedDuration: defaultValues.b2b.recommendedDuration,
  tasks: b2bTasks,
  hasCleaningMaterials: defaultValues.b2b.hasCleaningMaterials,
  execution: {
    date: null,
    time: null,
  },
  isExecutionDateTimeValidated: defaultValues.b2b.isExecutionDateTimeValidated,
  availabilityLoadingState: defaultValues.b2b.availabilityLoadingState,
  pricing: Record<B2BPricing>(pricingInitialState)(),
  nonStandardFrequencyShifts: defaultValues.b2b.nonStandardFrequencyShifts,
  preselectedAgentUuid: defaultValues.b2b.preselectedAgentUuid,
  agentPreference: defaultValues.b2b.agentPreference,
  previousAgents: List(),
  isChoosingAnyBatmaid: defaultValues.b2b.isChoosingAnyBatmaid,
  serviceLocationId: IS_BROWSER
    ? getServiceLocationIdFromUrl(window.location.href)
    : defaultValues.b2b.serviceLocationId,
  form: Record<RegistrationForm>(defaultValues.b2b.form)(),
  locationUuid: defaultValues.b2b.locationUuid,
  contactAfterBooking: defaultValues.b2b.contactAfterBooking,
  reservationUuid: defaultValues.b2b.reservationUuid,
  hasPets: defaultValues.b2b.hasPets,
  specialInstructions: defaultValues.b2b.specialInstructions,
  keyPick: defaultValues.b2b.keyPick,
  locationComment: defaultValues.b2b.locationComment,
  availablePaymentMethods: List(),
  selectedPaymentMethod: defaultValues.booking.selectedPaymentMethod,
  pricingLoadingState: defaultValues.b2b.pricingLoadingState,
  locations: Map(),
  locationsLoadingState: LOADING_STATE.IDLE,
  voucherToken: defaultValues.b2b.voucherToken,
  voucherMessage: defaultValues.b2b.voucherMessage,
  voucherStatus: defaultValues.b2b.voucherStatus,
  iDontKnowFrequencyNotification:
    defaultValues.b2b.iDontKnowFrequencyNotification,
  iDontKnowDurationNotification:
    defaultValues.b2b.iDontKnowDurationNotification,
  queryParams: defaultValues.b2b.queryParams,
  additionalInfo: defaultValues.getAQuote.additionalInfo,
  pricePlanUuid: defaultValues.b2b.pricePlanUuid,
  frequencies: defaultValues.b2b.frequencies,
  frequency: defaultValues.b2b.cleaningFrequency,
  frequenciesLoadingState: LOADING_STATE.IDLE,
  pricePlans: List(),
  pricePlansLoadingState: LOADING_STATE.IDLE,
});

const b2bReducer = (
  store: Record<B2BReducer> = initialState(),
  action: AnyAction,
): Record<B2BReducer> => {
  const { payload, type, params } = action;

  switch (type) {
    case B2B_ACTIONS.SET_FROM_LOCAL_STORAGE:
      return action.payload || store;
    case B2B_ACTIONS.SET_PREMISES: {
      return store.set("premises", payload);
    }
    case B2B_ACTIONS.SET_SURFACE_AREA: {
      return store.set("surfaceArea", payload);
    }
    case B2B_ACTIONS.SET_PREMISES_EQUIPMENTS: {
      return store.set(
        "premisesEquipments",
        premisesEquipmentsFactory(payload),
      );
    }
    case B2B_ACTIONS.SET_RECOMMENDED_DURATIONS_APPLIED: {
      return store.set("recommendedDurationsApplied", payload);
    }
    case B2B_ACTIONS.SET_RECOMMENDED_DURATION: {
      return store.set("recommendedDuration", payload);
    }
    case B2B_ACTIONS.SET_PREMISES_SERVICE: {
      return store.mergeDeepIn(["premisesServices"], { ...payload });
    }
    case B2B_ACTIONS.SET_TASK: {
      return store.mergeIn(["tasks", payload.service], {
        ...payload,
      });
    }
    case B2B_ACTIONS.SET_HAS_CLEANING_MATERIALS: {
      return store.set("hasCleaningMaterials", payload);
    }
    case B2B_ACTIONS.SET_CONTACT_AFTER_BOOKING: {
      return store.set("contactAfterBooking", payload);
    }
    case B2B_ACTIONS.SET_EXECUTION: {
      return store
        .set("execution", {
          ...store.get("execution"),
          ...payload,
        })
        .set("isExecutionDateTimeValidated", true);
    }
    case B2B_ACTIONS.SET_NON_STANDARD_FREQUENCY_CLEANINGS: {
      return store.set("nonStandardFrequencyShifts", payload);
    }
    case B2B_ACTIONS.SET_PRESELECTED_AGENT_UUID: {
      return store.set("preselectedAgentUuid", payload.preselectedAgentUuid);
    }
    case B2B_ACTIONS.SET_AGENT_PREFERENCE: {
      return store.set("agentPreference", payload.agentPreference);
    }
    case `${B2B_ACTIONS.FETCH_PREVIOUS_AGENTS}${ACTION_SUCCESS}`: {
      return store.set("previousAgents", agentsFactory(payload.data));
    }
    case B2B_ACTIONS.SET_IS_CHOOSING_ANY_BATMAID: {
      return store.set("isChoosingAnyBatmaid", payload);
    }
    case `${B2B_ACTIONS.FETCH_AVAILABILITY}${ACTION_PENDING}`:
    case `${B2B_ACTIONS.FETCH_CUSTOM_AVAILABILITY}${ACTION_PENDING}`:
    case `${B2B_ACTIONS.FETCH_AVAILABILITY_ADMIN}${ACTION_PENDING}`: {
      return store.set("availabilityLoadingState", LOADING_STATE.LOADING);
    }
    case `${B2B_ACTIONS.FETCH_AVAILABILITY}${ACTION_FAIL}`:
    case `${B2B_ACTIONS.FETCH_CUSTOM_AVAILABILITY}${ACTION_FAIL}`:
    case `${B2B_ACTIONS.FETCH_AVAILABILITY_ADMIN}${ACTION_FAIL}`: {
      return store.set("availabilityLoadingState", LOADING_STATE.FAILED);
    }
    case `${B2B_ACTIONS.FETCH_AVAILABILITY}${ACTION_SUCCESS}`: {
      return store
        .set(
          "calendar",
          bookingAvailabilityFactory({
            input: payload.data,
            isUserAdmin: false,
          }),
        )
        .set("availabilityLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${B2B_ACTIONS.FETCH_CUSTOM_AVAILABILITY}${ACTION_SUCCESS}`: {
      const isFirstRequest = !store.get("calendar").get("customDays");
      const factoredAvailability = bookingCustomAvailabilityFactory(
        payload.data,
        store.get("calendar"),
        params.chunkSize,
        params.isExtraRequest,
      );
      const defaultSelection = factoredAvailability.get("defaultSelection");
      const newExecution: B2BReducer["execution"] = (() => {
        if (!isFirstRequest || !defaultSelection) {
          // sometimes the defaultSelection is not in response
          return store.get("execution");
        }

        return {
          date: defaultSelection.split("T")[0],
          time: extractHourMinute(defaultSelection),
        };
      })();

      return store
        .set("calendar", factoredAvailability)
        .set("execution", newExecution)
        .set(
          "isExecutionDateTimeValidated",
          !!newExecution.date && !!newExecution.time,
        )
        .set("availabilityLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${B2B_ACTIONS.FETCH_AVAILABILITY_ADMIN}${ACTION_SUCCESS}`: {
      return store
        .set(
          "calendar",
          bookingAvailabilityFactory({
            input: payload.data,
            isUserAdmin: true,
          }),
        )
        .set("availabilityLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case B2B_ACTIONS.RESET_AVAILABILITY: {
      return store.set(
        "calendar",
        Record<BookingAvailability>(bookingAvailabilityInitialState)(),
      );
    }
    case B2B_ACTIONS.SET_WEEK_START_DATE: {
      return store.setIn(["calendar", "weekStartDate"], payload);
    }
    case B2B_ACTIONS.SET_REGISTRATION_FORM: {
      return store.mergeIn(["form"], payload);
    }
    case `${B2B_ACTIONS.MAKE_RESERVATION}${ACTION_SUCCESS}`: {
      return store.set("reservationUuid", payload.data.b2bReservationUuid);
    }
    case B2B_ACTIONS.SET_SERVICE_LOCATION_ID: {
      return store.set("serviceLocationId", payload);
    }
    case B2B_ACTIONS.SET_SPECIAL_INSTRUCTIONS: {
      return store.set("specialInstructions", payload);
    }
    case B2B_ACTIONS.SET_KEY_PICK: {
      return store.set("keyPick", payload);
    }
    case B2B_ACTIONS.SET_LOCATION_COMMENT: {
      return store.set("locationComment", payload);
    }
    case `${B2B_ACTIONS.FETCH_PAYMENT_METHODS}${ACTION_SUCCESS}`: {
      return store.set(
        "availablePaymentMethods",
        List<PAYMENT_METHODS>(payload.data),
      );
    }
    case B2B_ACTIONS.SET_PAYMENT_METHOD: {
      return store.set("selectedPaymentMethod", payload);
    }
    case `${B2B_ACTIONS.FETCH_PRICINGS}${ACTION_PENDING}`: {
      return store.set("pricingLoadingState", LOADING_STATE.LOADING);
    }
    case `${B2B_ACTIONS.FETCH_PRICINGS}${ACTION_SUCCESS}`: {
      return store
        .set(
          "pricing",
          pricingFactory({
            ...payload.data,
            isFetched: true,
          }),
        )
        .set("pricingLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${B2B_ACTIONS.FETCH_PRICINGS}${ACTION_FAIL}`: {
      return store.set("pricingLoadingState", LOADING_STATE.FAILED);
    }
    case `${B2B_ACTIONS.FETCH_PREMISES_SERVICES}${ACTION_SUCCESS}`: {
      return store
        .set("premisesServices", premisesServicesFactory(payload.data))
        .set("premisesServicesFetched", true);
    }
    case `${B2B_ACTIONS.REGISTER_CLIENT}${ACTION_SUCCESS}`: {
      return store.set("locationUuid", payload.data.locationUuid);
    }
    case `${B2B_ACTIONS.REGISTER_COMPANY}${ACTION_SUCCESS}`: {
      return store.set("locationUuid", payload.data.locationUuid);
    }
    case B2B_ACTIONS.SET_LOCATION_UUID: {
      return store.set("locationUuid", payload);
    }
    case `${B2B_ACTIONS.ADD_LOCATION}${ACTION_SUCCESS}`: {
      return store.set("locationUuid", payload.data.locationUuid);
    }
    case B2B_ACTIONS.FETCH_LOCATIONS: {
      return store.set("locationsLoadingState", LOADING_STATE.LOADING);
    }
    case `${B2B_ACTIONS.FETCH_LOCATIONS}${ACTION_SUCCESS}`: {
      return store
        .set("locations", locationsFactory(payload.data.locations))
        .set("locationsLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${B2B_ACTIONS.FETCH_LOCATIONS}${ACTION_FAIL}`: {
      return store.set("locationsLoadingState", LOADING_STATE.FAILED);
    }
    case `${B2B_ACTIONS.CANCEL_RESERVATION}${ACTION_NO_DATA}`:
    case `${B2B_ACTIONS.CANCEL_RESERVATION}${ACTION_FAIL}`: {
      return store.set("reservationUuid", defaultValues.b2b.reservationUuid);
    }
    case `${B2B_ACTIONS.FETCH_VOUCHER}${ACTION_SUCCESS}`: {
      return store
        .set("voucherToken", params.token)
        .set("voucherMessage", payload.data.translatedSuccessMessage)
        .set("voucherStatus", PROMO_CODE_NOTIFICATION.SUCCESS);
    }
    case `${B2B_ACTIONS.FETCH_VOUCHER}${ACTION_FAIL}`: {
      const { translatedErrorMessage, errors } = action as {
        translatedErrorMessage: string | undefined;
        errors: UnifiedErrors;
      } & AnyAction;
      const errorMessage = errors?.find(err => !!err.message)?.message;

      return store
        .set("voucherToken", defaultValues.b2b.voucherToken)
        .set("voucherMessage", translatedErrorMessage || errorMessage || "")
        .set("voucherStatus", PROMO_CODE_NOTIFICATION.FAILURE);
    }
    case B2B_ACTIONS.RESET_VOUCHER: {
      return store
        .set("voucherToken", defaultValues.b2b.voucherToken)
        .set("voucherMessage", defaultValues.b2b.voucherMessage)
        .set("voucherStatus", defaultValues.b2b.voucherStatus);
    }
    case B2B_ACTIONS.SET_VOUCHER: {
      return store
        .set("voucherMessage", payload.voucherMessage)
        .set("voucherStatus", payload.voucherStatus);
    }
    case B2B_ACTIONS.SET_I_DONT_KNOW_FREQUENCY_NOTIFICATION: {
      return store.set("iDontKnowFrequencyNotification", payload);
    }
    case B2B_ACTIONS.SET_I_DONT_KNOW_DURATION_NOTIFICATION: {
      return store.set("iDontKnowDurationNotification", payload);
    }
    case B2B_ACTIONS.SET_QUERY_PARAMS: {
      return store.set("queryParams", payload);
    }
    case B2B_ACTIONS.SET_ADDITIONAL_INFO: {
      return store.set("additionalInfo", payload);
    }
    case B2B_ACTIONS.LEAVE_PROCESS: {
      return initialState();
    }
    case `${B2B_ACTIONS.FETCH_FREQUENCIES}${ACTION_PENDING}`: {
      return store.set("frequenciesLoadingState", LOADING_STATE.LOADING);
    }
    case `${B2B_ACTIONS.FETCH_FREQUENCIES}${ACTION_SUCCESS}`: {
      return store
        .set("frequencies", frequenciesFactory(payload.data))
        .set("frequency", payload.data.default)
        .set("frequenciesLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${B2B_ACTIONS.FETCH_FREQUENCIES}${ACTION_FAIL}`: {
      return store
        .set("frequencies", List(defaultValues.b2b.frequencies))
        .set("frequenciesLoadingState", LOADING_STATE.FAILED);
    }
    case B2B_ACTIONS.SET_FREQUENCY: {
      const newPricePlanUuid = pricePlanFactory(
        payload.frequency,
        store.get("pricePlans"),
        payload.serviceLocationId,
      );

      return store
        .set("frequency", payload.frequency)
        .set("pricePlanUuid", newPricePlanUuid);
    }
    case `${B2B_ACTIONS.FETCH_PLANS}${ACTION_PENDING}`: {
      return store.set("pricePlansLoadingState", LOADING_STATE.LOADING);
    }
    case `${B2B_ACTIONS.FETCH_PLANS}${ACTION_FAIL}`: {
      return store.set("pricePlansLoadingState", LOADING_STATE.FAILED);
    }
    case `${B2B_ACTIONS.FETCH_PLANS}${ACTION_SUCCESS}`: {
      const plans = pricePlansFactory(payload.data);

      return store
        .set("pricePlansLoadingState", LOADING_STATE.SUCCEEDED)
        .set("pricePlans", plans);
    }
    default: {
      return store;
    }
  }
};

export { b2bReducer };
